import ObjectPath from 'object-path';

import { now } from "util/misc";
import * as Data from "./data"
import { ClientData, NameDetails, RemoteMediaData, RootInfo } from "./data";

const MAX_NAMES_SIZE = 500;
export const DEFAULT_NAMING_SERVER = "https://naming.moera.org/moera-naming";

export interface StoredData extends Data.ClientData {
    home?: Data.ClientHomeData & {
        location: string;
        nodeName?: string | null;
    };
    names?: Data.NameDetails[];
    remoteMedia?: Data.RemoteMediaData;
    roots?: Data.RootInfo[];
}

function buildData(
    currentRoot?: string | null,
    clientData?: ClientData,
    roots?: RootInfo[],
    names?: NameDetails[],
    remoteMedia?: RemoteMediaData | null,
    anonymousFullName?: string | null
): StoredData {
    let data = {};
    if (currentRoot) {
        data = {...clientData};
        ObjectPath.set(data, "home.location", currentRoot);
    }
    if (roots != null) {
        data = {...data, roots}
    }
    if (names != null) {
        data = {...data, names}
    }
    if (remoteMedia != null) {
        data = {...data, remoteMedia}
    }
    if (anonymousFullName != null) {
        data = {...data, anonymousFullName}
    }
    return data;
}

export function findNameServerUrl(settings: [string, string | null][] | null | undefined): string | null {
    if (settings == null) {
        return null;
    }
    const serverUrl = settings.find(([name]) => name === "naming.location")?.[1];
    if (serverUrl === DEFAULT_NAMING_SERVER) {
        return null;
    }
    return serverUrl ?? null;
}

export function loadData(): StoredData {
    const anonymousFullName = Data.getStorageItem("anonymousFullName");
    const homeRoot = Data.getStorageItem("currentRoot");
    const roots = Data.getStorageItem("roots") ?? [];
    if (!homeRoot) {
        return buildData(undefined, undefined, roots, undefined, undefined, anonymousFullName);
    }
    const clientData = Data.getStorageItem("clientData", homeRoot) ?? {};
    ObjectPath.set(clientData, "home.nodeName", Data.findRootName(roots, homeRoot));
    const names = Data.getNames(findNameServerUrl(clientData.settings));
    const remoteMedia = Data.getStorageItem("remoteMedia", homeRoot);
    return buildData(homeRoot, clientData, roots, names, remoteMedia, anonymousFullName);
}

export function storeData(data: StoredData): Data.RootInfo[] {
    let homeRoot = Data.getStorageItem("currentRoot");
    let roots = Data.getStorageItem("roots") ?? [];
    const location = ObjectPath.get<string | null>(data, "home.location", null);
    const nodeName = ObjectPath.get<string | null>(data, "home.nodeName", null);
    if (location) {
        if (homeRoot !== location) {
            Data.setStorageItem("currentRoot", null, location);
            homeRoot = location;
        }
        roots = Data.setRoot(roots, homeRoot, nodeName);
        Data.setStorageItem("roots", null, roots);
    }
    if (homeRoot) {
        storeDataForRoot(homeRoot, data);
    }

    return roots;
}

export function storeDataForRoot(homeRoot: string, data: StoredData): void {
    let clientData = Data.getStorageItem("clientData", homeRoot);
    clientData = {
        ...clientData,
        ...data
    };
    ObjectPath.del(clientData, "home.location");

    const storedClientData = {...clientData};
    ObjectPath.del(storedClientData, "home.nodeName");
    Data.setStorageItem("clientData", homeRoot, storedClientData);
}

export function deleteData(location: string | null): boolean {
    let homeRoot = Data.getStorageItem("currentRoot");
    let roots = Data.getStorageItem("roots") ?? [];
    if (!location) {
        location = homeRoot;
    }
    if (roots.find(r => r.url === location) == null && location !== homeRoot) {
        return false;
    }
    roots = roots.filter(r => r.url !== location);
    Data.setStorageItem("roots", null, roots);
    Data.removeStorageItem("clientData", location);
    Data.removeStorageItem("remoteMedia", location);

    if (location === homeRoot || homeRoot == null) {
        if (roots.length === 0) {
            Data.removeStorageItem("currentRoot");
            return homeRoot != null;
        }
        homeRoot = roots[roots.length - 1].url;
        Data.setStorageItem("currentRoot", null, homeRoot);
        return true;
    }

    return false;
}

export function switchData(location: string): boolean {
    let homeRoot = Data.getStorageItem("currentRoot");
    let roots = Data.getStorageItem("roots") ?? [];

    const root = roots.find(r => r.url === location);
    if (!location || location === homeRoot || root == null) {
        return false;
    }
    Data.setStorageItem("currentRoot", null, location);

    return true;
}

export function storeName(serverUrl: string | null, name: string, nodeUri: string, updated: number): void {
    if (serverUrl === DEFAULT_NAMING_SERVER) {
        serverUrl = null;
    }
    let names = Data.getStorageItem("names", serverUrl) ?? [];
    names = names.filter(info => info.name !== name);
    updated ??= now();
    names.push({name, nodeUri, updated});
    names.sort((a, b) => a.updated - b.updated);
    if (names.length > MAX_NAMES_SIZE) {
        names.splice(0, names.length - MAX_NAMES_SIZE);
    }
    Data.setStorageItem("names", serverUrl, names);
}

export function loadNames(serverUrl: string | null): Data.NameDetails[] {
    if (serverUrl === DEFAULT_NAMING_SERVER) {
        serverUrl = null;
    }
    return Data.getNames(serverUrl);
}

function isRemoteMediaEmpty(remoteMedia: Data.RemoteMediaData): boolean {
    for (const node of Object.values(remoteMedia)) {
        if (node != null && Object.keys(node).length > 0) {
            return false;
        }
    }
    return true;
}

export function storeRemoteMedia(remoteMedia: Data.RemoteMediaData): void {
    const homeRoot = Data.getStorageItem("currentRoot");
    if (!homeRoot) {
        return;
    }
    if (!isRemoteMediaEmpty(remoteMedia)) {
        Data.setStorageItem("remoteMedia", homeRoot, remoteMedia);
    } else {
        Data.removeStorageItem("remoteMedia", homeRoot);
    }
}

export function storeAnonymousFullName(anonymousFullName: string | null): void {
    Data.setStorageItem("anonymousFullName", null, anonymousFullName ?? "");
}
